import { useState, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useStore } from '@/store';
import { selectAucResults } from '@/store/selectors';
import { Button } from '@/components/ui/Button';
import { logLinearRegression } from '@bioavail/shared-logic/auc/regression';
import { estimateLambdaZ } from '@bioavail/shared-logic/auc/lambdaZ';
import { computeAbsoluteF } from '@bioavail/shared-logic/bioavailability/absoluteF';
import {
  normalizeConcentration,
  normalizeTime,
  normalizeDose,
} from '@bioavail/shared-logic/units/normalize';
import { ArmRole } from '@bioavail/shared-types';
import { formatNumber } from '@/utils/format';

/** Manual terminal-point selection (F-011). */
export function LambdaZEditor({ armId }: { armId: string }) {
  const study = useStore((s) => s.studies.find((x) => x.id === s.activeStudyId));
  const arm = study?.arms.find((a) => a.id === armId);
  const setLambdaZRange = useStore((s) => s.setLambdaZRange);
  const updateArm = useStore((s) => s.updateArm);
  const aucResults = useStore(useShallow(selectAucResults));
  const [selected, setSelected] = useState<Set<number>>(
    new Set(
      arm
        ? arm.dataPoints
            .map((p, i) => ({ p, i }))
            .filter(({ p }) => p.concentration > 0)
            .slice(-3)
            .map(({ i }) => i)
        : [],
    ),
  );

  const fit = useMemo(() => {
    if (!arm) return null;
    const points = [...arm.dataPoints]
      .map((p, i) => ({ p, i }))
      .filter(({ i }) => selected.has(i))
      .filter(({ p }) => p.concentration > 0)
      .map(({ p }) => ({ time: p.time, concentration: p.concentration }));
    if (points.length < 3) return null;
    try {
      return logLinearRegression(points);
    } catch {
      return null;
    }
  }, [arm, selected]);

  // Auto-vs-manual λ_z sensitivity (F-070). Only relevant when a manual
  // override is applied — compares the auto-detected fit to the committed
  // selection and surfaces the resulting ΔF.
  const sensitivity = useMemo(() => {
    if (!arm || !study) return null;
    if (!arm.lambdaZManualOverride) return null;
    const auc = aucResults.find((a) => a.armId === armId);
    if (!auc) return null;

    const normalizedPoints = arm.dataPoints.map((p) => ({
      time: normalizeTime(p.time, arm.timeUnit),
      concentration: normalizeConcentration(p.concentration, arm.concentrationUnit),
      isBlq: p.isBlq,
    }));

    const fitLambda = (
      ...indices: (number | undefined)[]
    ): { lambdaZ: number; extrap: number } | null => {
      try {
        const res = estimateLambdaZ(normalizedPoints, auc.aucZeroToT, ...indices);
        return { lambdaZ: res.lambdaZ, extrap: res.aucExtrapolated };
      } catch {
        return null;
      }
    };

    const auto = fitLambda();
    const manual = fitLambda(arm.lambdaZStartIndex, arm.lambdaZEndIndex);

    if (!auto || !manual) return { autoUnavailable: true } as const;
    const { lambdaZ: autoLambdaZ, extrap: autoExtrap } = auto;
    const { lambdaZ: manualLambdaZ, extrap: manualExtrap } = manual;

    // Other arm + doses for the F computation.
    const otherArm = study.arms.find((a) => a.id !== armId);
    const otherAuc = otherArm ? aucResults.find((a) => a.armId === otherArm.id) : undefined;
    if (!otherArm || !otherAuc) return { autoUnavailable: false, autoLambdaZ, manualLambdaZ } as const;

    const doseThis = normalizeDose(arm.dose, arm.doseUnit);
    const doseOther = normalizeDose(otherArm.dose, otherArm.doseUnit);
    const aucOther = otherAuc.aucZeroToInf > 0 ? otherAuc.aucZeroToInf : otherAuc.aucZeroToT;

    const computeF = (extrapThis: number): number | null => {
      const aucThis = auc.aucZeroToT + extrapThis;
      try {
        if (arm.role === ArmRole.Test) {
          return computeAbsoluteF({
            aucTest: aucThis,
            aucRef: aucOther,
            doseTest: doseThis,
            doseRef: doseOther,
          }).fDoseCorrected;
        }
        return computeAbsoluteF({
          aucTest: aucOther,
          aucRef: aucThis,
          doseTest: doseOther,
          doseRef: doseThis,
        }).fDoseCorrected;
      } catch {
        return null;
      }
    };

    const fAuto = computeF(autoExtrap);
    const fManual = computeF(manualExtrap);
    return { autoUnavailable: false, autoLambdaZ, manualLambdaZ, fAuto, fManual } as const;
  }, [arm, study, aucResults, armId]);

  if (!arm) return null;

  function toggle(i: number) {
    const next = new Set(selected);
    if (next.has(i)) next.delete(i);
    else next.add(i);
    setSelected(next);
  }

  function apply() {
    const indices = [...selected].sort((a, b) => a - b);
    if (indices.length >= 3) {
      setLambdaZRange({ armId, start: indices[0]!, end: indices[indices.length - 1]! });
    }
  }

  function autoDetect() {
    if (!arm) return;
    updateArm(armId, {
      lambdaZManualOverride: false,
      lambdaZStartIndex: undefined,
      lambdaZEndIndex: undefined,
    });
    const last3 = arm.dataPoints
      .map((p, i) => ({ p, i }))
      .filter(({ p }) => p.concentration > 0)
      .slice(-3)
      .map(({ i }) => i);
    setSelected(new Set(last3));
  }

  return (
    <div className="flex-col-gap-3">
      <p className="field-hint">
        Select terminal-phase points to fit λ_z. R² updates live.
      </p>
      {arm.dataPoints.length === 0 ? (
        <p className="empty-state">No concentration-time points on this arm yet.</p>
      ) : (
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th aria-label="Use" />
                <th>Time</th>
                <th>Concentration</th>
              </tr>
            </thead>
            <tbody>
              {arm.dataPoints.map((p, i) => (
                <tr key={i}>
                  <td className="text-center">
                    <input
                      type="checkbox"
                      checked={selected.has(i)}
                      onChange={() => toggle(i)}
                      aria-label={`Use point ${i + 1}`}
                      disabled={p.concentration <= 0}
                    />
                  </td>
                  <td>{formatNumber(p.time)}</td>
                  <td>{formatNumber(p.concentration)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="field-hint">
        R² = {fit ? formatNumber(fit.r2) : '—'} · λ_z ={' '}
        {fit && fit.slope < 0 ? formatNumber(-fit.slope) : '—'}
        {fit && fit.r2 < 0.85 ? <span className="inline-badge">low R²</span> : null}
      </p>
      {sensitivity && !sensitivity.autoUnavailable ? (
        <div
          className="stat-secondary-grid cols-3"
          role="group"
          aria-label="λ_z sensitivity comparison"
        >
          <div className="stat-secondary">
            <span className="stat-secondary-label">Auto λ_z</span>
            <span className="stat-secondary-value">
              {sensitivity.autoLambdaZ.toFixed(4)}
              {sensitivity.fAuto != null ? (
                <span className="stat-secondary-sub"> F≈{sensitivity.fAuto.toFixed(3)}</span>
              ) : null}
            </span>
          </div>
          <div className="stat-secondary">
            <span className="stat-secondary-label">Your selection</span>
            <span className="stat-secondary-value">
              {sensitivity.manualLambdaZ.toFixed(4)}
              {sensitivity.fManual != null ? (
                <span className="stat-secondary-sub"> F≈{sensitivity.fManual.toFixed(3)}</span>
              ) : null}
            </span>
          </div>
          {sensitivity.fAuto != null && sensitivity.fManual != null ? (
            <div className="stat-secondary">
              <span className="stat-secondary-label">ΔF (auto − manual)</span>
              <span className="stat-secondary-value">
                {(sensitivity.fAuto - sensitivity.fManual).toFixed(3)}
              </span>
            </div>
          ) : null}
        </div>
      ) : null}
      {sensitivity && sensitivity.autoUnavailable ? (
        <p className="field-hint">
          Auto-detect unavailable: fewer than three positive concentrations remain after the
          peak, so a log-linear terminal slope cannot be fitted. Add later terminal samples
          (or lower BLQ thresholds) to enable auto-detect, or pick points manually above.
        </p>
      ) : null}
      <div className="flex-row-gap-2">
        <Button variant="primary" size="sm" onClick={apply} disabled={!fit}>
          Apply selection
        </Button>
        <Button variant="secondary" size="sm" onClick={autoDetect}>
          Auto-detect
        </Button>
      </div>
    </div>
  );
}
